import { defineStore } from "pinia";
import PROFESSION_NAV_LINK_TASKS from "./professionNavLinkTasks.graphql";
import { importLinkMap } from "./importLinkMap";
import { FEATURES } from "~/config/pages/features.config";
import { capitalizeFirstLetter } from "~/helpers/jsutil";
import {
  FOOTER_ITEM_COUNT,
  HEADER_MENU_LIMIT,
  PRICES_MENU_LIMIT,
  getPageType,
  isValidSeoName,
  monthKey,
  normalizePath,
  pickRotation,
  resolveNavLinks,
} from "~/helpers/professionNavLinks";

async function fetchProfessionTasks(context, professionSeoName) {
  try {
    const result = await context.$query(PROFESSION_NAV_LINK_TASKS, {
      professionSeoName,
    });
    return result.popularProfessionTasksByProfession ?? [];
  } catch (err) {
    context.$logger.warn("Failed to load profession nav link tasks", err);
    return [];
  }
}

function resolvePricesItems(context, professionSeoName, professionTasks) {
  const currentPath = normalizePath(context.route.path);

  return professionTasks
    .map((professionTask) => ({
      id: `dyn-nav-${professionSeoName}-arak-${professionTask.seoName}`,
      text: capitalizeFirstLetter(professionTask.name),
      to: context.localePath({
        name: "prices",
        params: { professionTask: professionTask.seoName },
      }),
    }))
    .filter(({ to }) => normalizePath(to) !== currentPath)
    .slice(0, PRICES_MENU_LIMIT);
}

export const useProfessionNavLinksStore = defineStore("professionNavLinks", {
  state: () => ({
    professionSeoName: null,
    headerItems: [],
    pricesItems: [],
    footerItems: [],
  }),
  actions: {
    async load(context, { professionSeoName, professionTasks = null }) {
      this.$reset();

      if (
        !context.$branding.hasFeature(FEATURES.DYNAMIC_NAV_LINKS) ||
        !isValidSeoName(professionSeoName)
      ) {
        return;
      }

      let linkMap;
      try {
        linkMap = await importLinkMap(professionSeoName);
      } catch {
        return;
      }

      const tasks =
        professionTasks ??
        (await fetchProfessionTasks(context, professionSeoName));

      const resolveOptions = {
        localePath: context.localePath,
        currentPath: context.route.path,
        professionSeoName,
      };

      const footerRotation = linkMap.footer_rotation ?? {};
      const isFooterEligible = (
        footerRotation.eligible_page_types ?? []
      ).includes(getPageType(context.route.name));

      this.$patch({
        professionSeoName,
        headerItems: resolveNavLinks(linkMap.header_menu, {
          ...resolveOptions,
          idPrefix: "dyn-nav",
        }).slice(0, HEADER_MENU_LIMIT),
        pricesItems: resolvePricesItems(context, professionSeoName, tasks),
        footerItems: isFooterEligible
          ? pickRotation(
              resolveNavLinks(footerRotation.items, {
                ...resolveOptions,
                idPrefix: "dyn-footer",
              }),
              FOOTER_ITEM_COUNT,
              `${normalizePath(context.route.path)}|${monthKey()}`
            )
          : [],
      });
    },
  },
});
